import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FavoriteStop, JourneyPlannerFacade, JourneyPlannerService } from '@ridango/components/lib/journey-planner';
import { BaseComponent } from '@ridango/core';
import { takeUntil, tap } from 'rxjs/operators';
import { JourneyPlannerFavoritesListModalComponent } from '../journey-planner-favorites-list-modal/journey-planner-favorites-list-modal.component';
import { JourneyPlannerLocationMark } from '../journey-planner-location-mark/journey-planner-location-mark.component';
import Env from '@ridango/core/env';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';

@Component({
    selector: 'rwidget-journey-planner-favorite-list',
    templateUrl: './journey-planner-favorite-list.component.html',
    styleUrls: ['./journey-planner-favorite-list.component.scss']
})
export class JourneyPlannerFavoriteListComponent extends BaseComponent implements OnInit, OnDestroy {
    readonly BUTTON_LABEL_EDIT = 'Edit';
    readonly BLOCK_NAME = 'My favorite journeys';

    @Input() isShowHeader = true;

    @Input() isShowLocationRemoveIcon = false;

    @Output()
    public clickOnLocation: EventEmitter<FavoriteStop> = new EventEmitter();

    @Output()
    public removedLocations: EventEmitter<FavoriteStop[]> = new EventEmitter();

    public favoriteStops: FavoriteStop[] = [];

    public tempDeletedFavoriteStops: FavoriteStop[] = [];

    public favoriteStopsFormatted: JourneyPlannerLocationMark[] = [];

    constructor(
        @Inject('ENV') public env: Env,
        private modalCtrl: ModalController,
        private journeyPlannerFacade: JourneyPlannerFacade,
        private journeyPlannerService: JourneyPlannerService,
        private analyticsService: AnalyticsService
    ) {
        super();
    }

    /* Hooks */
    ngOnInit() {
        this.initialize();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    /* Methods */
    private initialize(): void {
        this.journeyPlannerFacade.favoriteStops$
            .pipe(
                tap((favoriteStops) => {
                    this.favoriteStopsFormatted = favoriteStops.map((stop) => ({
                        origin: stop.fromPlaceName,
                        destination: stop.toPlaceName,
                        id: stop.id
                    }));
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe((favoriteStops) => {
                this.favoriteStops = favoriteStops;
            });
    }

    public async handleEditFavorites(): Promise<void> {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.JOURNEY_PLANNER,
            name: `${this.BLOCK_NAME} - ${this.BUTTON_LABEL_EDIT}`
        });
        const modal = await this.modalCtrl.create({
            component: JourneyPlannerFavoritesListModalComponent,
            cssClass: 'journey-planner-favorite-list-modal'
        });

        await modal.present();
    }

    public handleClickOnLocation(location: JourneyPlannerLocationMark): void {
        this.clickOnLocation.emit(this.favoriteStops.find((stop) => stop.id === location.id));
    }

    public handleRemoveLocations(location: JourneyPlannerLocationMark): void {
        const currentStop = this.favoriteStops.find((stop) => stop.id === location.id);
        this.tempDeletedFavoriteStops.push(currentStop);
        this.favoriteStops = this.favoriteStops.filter((stop) =>
            this.journeyPlannerService.filterCheckFavoriteStops(stop, currentStop)
        );
        this.favoriteStopsFormatted = this.favoriteStops.map((stop) => ({
            origin: stop.fromPlaceName,
            destination: stop.toPlaceName,
            id: stop.id
        }));
        this.removedLocations.emit(this.tempDeletedFavoriteStops);
    }
}
